import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { formatPrice } from '../ui/price';
import { deleteImages } from '../db/images';
import { applyImport, type ImportChoice } from '../db/imports';
import { usePlatform } from '../platform/context';
import type { Item } from '../db/schema';
import { runImport, STARTING, type ImportResult, type Progress } from './run';
import { messageFor } from './useImport';
import './ReimportDialog.css';

/**
 * Re-running the import against an item's stored URL.
 *
 * Nothing is written until the user says so — the doc calls for a diff of what
 * would change, and a listing that has been rewritten since you saved it can
 * change in ways you'd rather keep out.
 *
 * `notes` is not in the diff, cannot be checked, and is not written. There is
 * no code path here that could touch it (invariant 4).
 */

type Row = {
  key: keyof ImportChoice;
  label: string;
  before: string;
  after: string;
};

export function ReimportDialog({
  item,
  url,
  onClose,
}: {
  item: Item;
  url: string;
  onClose: () => void;
}) {
  const platform = usePlatform();

  const [progress, setProgress] = useState<Progress>(STARTING);
  const [result, setResult] = useState<ImportResult | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [chosen, setChosen] = useState<Set<keyof ImportChoice>>(new Set());
  const [applying, setApplying] = useState(false);

  // StrictMode runs effects twice in development. Without this the pipeline
  // fires twice and downloads every photo twice.
  const started = useRef(false);
  // Read in cleanup, which must not close over a stale `result`.
  const unapplied = useRef<string[]>([]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const found = await runImport({ platform, url, onProgress: setProgress });
        unapplied.current = found.imageIds;
        setResult(found);
        setChosen(new Set(changesIn(item, found).map((row) => row.key)));
      } catch (thrown) {
        setError(messageFor(thrown));
      }
    })();
  }, [platform, url, item]);

  // Closing without applying leaves downloaded images with nothing pointing at
  // them. They go with the dialog.
  useEffect(() => {
    return () => {
      if (unapplied.current.length > 0) void deleteImages(unapplied.current);
    };
  }, []);

  async function apply() {
    if (result === undefined) return;

    setApplying(true);
    // Whatever `applyImport` keeps is no longer this dialog's to clean up; it
    // deletes the photos itself when they aren't wanted.
    unapplied.current = [];

    try {
      await applyImport(item.id, result, {
        title: chosen.has('title'),
        description: chosen.has('description'),
        price: chosen.has('price'),
        photos: chosen.has('photos'),
      });
      onClose();
    } catch {
      setError("Those changes couldn't be saved.");
      setApplying(false);
    }
  }

  function toggle(key: keyof ImportChoice) {
    setChosen((was) => {
      const next = new Set(was);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const rows = result === undefined ? [] : changesIn(item, result);

  return (
    <Dialog title="Re-import" onClose={onClose}>
      <div className="dialog__form">
        {error !== undefined && (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}

        {error === undefined && result === undefined && (
          <p className="reimport__status" role="status">
            {progress.saved === undefined
              ? 'Reading the page…'
              : `Saving photos… ${progress.saved.done} of ${progress.saved.total}`}
          </p>
        )}

        {result !== undefined && rows.length === 0 && (
          <p className="reimport__status">Nothing has changed since you saved this.</p>
        )}

        {rows.length > 0 && (
          <>
            <p className="reimport__note">
              Your notes are never touched by an import.
            </p>

            <ul className="reimport__rows">
              {rows.map((row) => (
                <li key={row.key} className="reimport__row">
                  <label className="reimport__check">
                    <input
                      type="checkbox"
                      checked={chosen.has(row.key)}
                      onChange={() => toggle(row.key)}
                    />
                    <span className="section-label">{row.label}</span>
                  </label>

                  <p className="reimport__before">{row.before}</p>
                  <p className="reimport__after">{row.after}</p>
                </li>
              ))}
            </ul>
          </>
        )}

        <DialogActions>
          <Button variant="quiet" onClick={onClose} disabled={applying}>
            {rows.length === 0 ? 'Close' : 'Cancel'}
          </Button>
          {rows.length > 0 && (
            <Button onClick={() => void apply()} disabled={applying || chosen.size === 0}>
              Apply
            </Button>
          )}
        </DialogActions>
      </div>
    </Dialog>
  );
}

/**
 * Only fields that would actually change. An import that came back with no
 * description doesn't get to blank the one already there, so an empty result
 * is not a change.
 */
function changesIn(item: Item, result: ImportResult): Row[] {
  const rows: Row[] = [];

  const title = result.title?.trim();
  if (title !== undefined && title !== '' && title !== item.title) {
    rows.push({ key: 'title', label: 'Title', before: item.title, after: title });
  }

  const description = result.description.trim();
  if (description !== '' && description !== item.description) {
    rows.push({
      key: 'description',
      label: 'Description',
      before: item.description === '' ? 'Nothing yet' : item.description,
      after: description,
    });
  }

  if (result.price !== undefined && !samePrice(item, result)) {
    rows.push({
      key: 'price',
      label: 'Price',
      before: formatPrice(item.price, item.currency) ?? 'Nothing yet',
      after: formatPrice(result.price, result.currency) ?? String(result.price),
    });
  }

  if (result.imageIds.length > 0) {
    rows.push({
      key: 'photos',
      label: 'Photos',
      before: `${item.imageIds.length + (item.coverImageId === undefined ? 0 : 1)} now`,
      // Added, not swapped: photos you uploaded yourself stay.
      after: `${result.imageIds.length} more added`,
    });
  }

  return rows;
}

function samePrice(item: Item, result: ImportResult): boolean {
  return item.price === result.price && item.currency === result.currency;
}
